import React, { useMemo } from 'react';
import { Divider, Grid } from '@mui/material';
import { useTranslation } from '@stateManagement/TranslationContext';
import { Redirect } from '@components/Redirect';
import { getTranslation } from '@helpers/languageTranslations';
import { languageRouteTypes } from '@helpers/languageRoutes';
import { cleanEscapes } from '@helpers/string';
import { getPressReleaseStructuredDataScript } from '@helpers/articleScriptHelper';
import { ArticleSectionPageHelmet } from '@components/BlogSection/ArticleSectionPageHelmet';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { breakpoints, colors, Container, Heading, LazyLoadContainer } from '@web-for-marketing/react-ui';
import { BlogCard2 } from '@components/BlogCards/BlogCard2';
import { getLanguageRouteAndPrefixByCodeAndRouteKey } from '@helpers/languages';
import { useAtom } from 'jotai';
import { selectedLanguageAtom } from '@atoms/appSettings';
import { MediaReleaseContentArea } from '@components/MediaRelease/MediaReleaseContentArea';
import { MediaReleaseHeader } from '@components/MediaRelease/MediaReleaseHeader';

const classes = {
    relatedPostBackground: {
        margin: '0',
        backgroundColor: colors.background.lighter,
        [`@media (max-width: ${breakpoints.md})`]: {
            padding: 0,
            boxShadow: 'none',
            flexDirection: 'column-reverse',
        },
        [`@media (max-width: ${breakpoints.md})`]: {
            '&:before': {
                content: 'none',
            },
        },
    },
    relatedPostContainer: {
        padding: '5.6rem 6.4rem',
        [`@media (max-width: ${breakpoints.lg})`]: {
            padding: '5.6rem 0',
        },
        [`@media (max-width: ${breakpoints.sm})`]: {
            padding: '1.6rem 0',
        },
    },
    container: {
        padding: '0 6.4rem',
        [`@media (max-width: ${breakpoints.lg})`]: {
            padding: '0',
        },
    },
    relatedPostTitle: {
        marginBottom: '3.2rem',
        [`@media (max-width: ${breakpoints.sm})`]: {
            marginBottom: '1.6rem',
        },
    },
    blogHeaderBackgroundContainer: {
        paddingTop: '1.2rem',
        backgroundColor: colors.background.light,
        [`@media (max-width: ${breakpoints.lg})`]: {
            backgroundColor: colors.background.white,
            paddingTop: 0,
        },
    },
    dividerContainer: {
        display: 'block',
        [`@media (max-width: ${breakpoints.md})`]: {
            display: 'none',
        },
    },
    divider: {
        borderWidth: '1.5px',
    },
} as const;

export default function MediaRelease(): React.ReactElement | null {
    const { selectedLanguageTranslation } = useTranslation();
    const { prefixStringWithBaseUrl, pressRelease: mediaRelease, mediaUrlPrefix, fullUrl } = useStaticValues();
    const [selectedLanguage] = useAtom(selectedLanguageAtom);

    const cleanFullUrl = cleanEscapes(fullUrl, '"');
    const mediaReleaseArticle = mediaRelease.articleData;
    const recentNewsTranslation = useMemo(
        () => getTranslation(selectedLanguageTranslation, 'pressRelease', 'relatedNews'),
        [selectedLanguageTranslation]
    );

    const languageMediaReleaseLink = useMemo(
        () => getLanguageRouteAndPrefixByCodeAndRouteKey(selectedLanguage, languageRouteTypes.PRESS_RELEASE),
        [selectedLanguage]
    );

    return mediaReleaseArticle ? (
        mediaReleaseArticle.pressRelease.redirectUrl ? (
            <Redirect statusCode={200} redirectUrl={mediaReleaseArticle.pressRelease.redirectUrl} />
        ) : (
            <>
                <ArticleSectionPageHelmet
                    article={mediaRelease}
                    articleType='pressRelease'
                    noIndex={mediaReleaseArticle.pressRelease.noIndex}
                    script={getPressReleaseStructuredDataScript(
                        mediaReleaseArticle.pressRelease,
                        mediaUrlPrefix,
                        prefixStringWithBaseUrl,
                        cleanFullUrl
                    )}
                />
                <div css={classes.blogHeaderBackgroundContainer}>
                    <MediaReleaseHeader mediaRelease={mediaReleaseArticle.pressRelease} />
                </div>
                <Container>
                    <div css={classes.container}>
                        <div css={classes.dividerContainer}>
                            <Divider css={classes.divider}></Divider>
                        </div>
                    </div>
                </Container>
                <Container>
                    <div css={classes.container}>
                        <MediaReleaseContentArea
                            mediaRelease={mediaReleaseArticle.pressRelease}
                            inTheNews={mediaReleaseArticle.inTheNews}
                        />
                    </div>
                </Container>
                <div css={classes.relatedPostBackground}>
                    <Container component='section'>
                        <div css={classes.relatedPostContainer}>
                            <Heading variant='h2' color='activePrimary' css={classes.relatedPostTitle}>
                                {recentNewsTranslation}
                            </Heading>
                            <LazyLoadContainer component={Grid} spacing={{ xs: 2, md: 4 }} container>
                                {mediaReleaseArticle.relatedNews.map((card) => (
                                    <Grid key={card.id} size={{ xs: 12, md: 6 }}>
                                        <BlogCard2
                                            title={card.title}
                                            linkPath={`${languageMediaReleaseLink}${card.slug}`}
                                            image={{
                                                imagePath: prefixStringWithBaseUrl(card.cardImageUrl),
                                                imageAlt: card.cardImageAlt,
                                            }}
                                            publishedDateUtc={card.publishedDateUtc ? card.publishedDateUtc : ''}
                                            readMinutes={card.readMinutes}
                                            ariaLabel={`Read more about ${card.title}`}
                                            lazyLoad={true}
                                        />
                                    </Grid>
                                ))}
                            </LazyLoadContainer>
                        </div>
                    </Container>
                </div>
            </>
        )
    ) : null;
}
